/**
 * Native Cart Drawer — PWT Sitespeed
 * Vanilla JS, no jQuery dependency
 */
(function () {
  'use strict';

  var SELECTORS = {
    drawer: '#NativeCartDrawer',
    overlay: '.native-cart-overlay',
    panel: '.native-cart-panel',
    closeBtn: '[data-action="close-drawer"]',
    cartItems: '[data-cart-items]',
    cartCount: '[data-cart-count]',
    cartSubtotal: '[data-cart-subtotal]',
    cartDiscounts: '[data-cart-discounts]',
    rewardBar: '.reward-bar',
    rewardFill: '[data-reward-fill]',
    rewardMessage: '[data-reward-message]',
    rewardTicks: '[data-reward-tick]',
    rewardLabels: '[data-reward-label]',
    noteWrapper: '[data-cart-note-wrapper]',
    noteInput: '[data-cart-note-input]',
    headerBubble: '.cart-link__bubble',
    headerCartLinks: '.js-native-cart-open'
  };

  var REDO_PRODUCT_ID, REDO_VARIANT_ID;
  var REDO_ENABLED = false, UPSELL_ENABLED = false, NOTE_ENABLED = false;
  var UPSELL_COUNT = 4;
  var EXCLUDED_PRODUCT_IDS = [9814282502454];
  var TIERS = [];

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var ROUTES = {
    cart: ROOT + 'cart.js',
    cartAdd: ROOT + 'cart/add.js',
    cartChange: ROOT + 'cart/change.js',
    cartUpdate: ROOT + 'cart/update.js'
  };

  var drawer, isOpen = false, noteTimer = null, redoAddInProgress = false;

  // ─── Helpers ───────────────────────────────────────
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return (root || document).querySelectorAll(sel); }

  function formatMoney(cents) {
    var format = (window.theme && window.theme.settings && window.theme.settings.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var amountNoDecimals = Math.floor(cents / 100);
    var amountWithComma = amount.replace('.', ',');

    return format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount}}', amount);
  }

  function buildCodeBox(code) {
    return '<span class="reward-bar__code-box" data-copy-code="' + code + '" onclick="navigator.clipboard.writeText(this.dataset.copyCode);this.classList.add(\'is-copied\');var el=this;setTimeout(function(){el.classList.remove(\'is-copied\')},1500)" title="click to copy">' +
      '<span class="reward-bar__code-text">' + code + '</span>' +
      '<span class="reward-bar__code-copy">copy</span>' +
      '<span class="reward-bar__code-copied">copied!</span>' +
    '</span>';
  }

  function fetchJSON(url, opts) {
    opts = opts || {};
    opts.headers = opts.headers || {};
    opts.headers['Accept'] = 'application/json';
    if (opts.method === 'POST') {
      opts.headers['Content-Type'] = 'application/json';
    }
    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, 10000);
    opts.signal = controller.signal;
    return fetch(url, opts).then(function (r) {
      clearTimeout(timeoutId);
      return r.text().then(function (text) {
        var data;
        try { data = JSON.parse(text); } catch (e) {
          return Promise.reject({ description: 'Unexpected server response. Please try again.', status: r.status });
        }
        if (!r.ok) {
          if (!data.description) data.description = data.message || 'Something went wrong (' + r.status + ')';
          return Promise.reject(data);
        }
        return data;
      });
    }).catch(function (err) {
      clearTimeout(timeoutId);
      if (err && err.name === 'AbortError') {
        return Promise.reject({ description: 'Request timed out. Please try again.' });
      }
      if (!err || !err.description) {
        return Promise.reject({ description: (err && err.message) || 'Something went wrong. Please try again.' });
      }
      return Promise.reject(err);
    });
  }

  // ─── Cart operation queue ─────────────────────────
  var _cartQueue = Promise.resolve();
  function enqueueCartOp(fn) {
    _cartQueue = _cartQueue.then(fn, fn);
    return _cartQueue;
  }

  // ─── Drawer open/close ─────────────────────────────

  function openDrawer() {
    if (isOpen) return;
    isOpen = true;
    drawer.classList.add('is-open');
    document.body.classList.add('native-cart-open');
    // Focus trap: focus the close button
    var closeBtn = qs('.native-cart-close', drawer);
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 100);
  }

  function closeDrawer() {
    if (!isOpen) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    document.body.classList.remove('native-cart-open');
  }

  // ─── Cart API ──────────────────────────────────────

  function fetchCart() {
    return fetchJSON(ROUTES.cart);
  }

  function addToCart(variantId, qty, properties, sellingPlan) {
    return enqueueCartOp(function () {
      var item = { id: variantId, quantity: qty || 1 };
      if (properties) item.properties = properties;
      if (sellingPlan) item.selling_plan = sellingPlan;
      drawer.classList.add('is-loading');
      return fetchJSON(ROUTES.cartAdd, {
        method: 'POST',
        body: JSON.stringify({ items: [item] })
      }).then(function () {
        return refreshDrawer();
      }).then(function () {
        openDrawer();
        drawer.classList.remove('is-loading');
      }).catch(function (err) {
        console.error('Add to cart error:', err);
        drawer.classList.remove('is-loading');
        return refreshDrawer().then(function () {
          openDrawer();
          showCartError(err);
        });
      });
    });
  }

  function updateQuantity(key, qty) {
    return enqueueCartOp(function () {
      drawer.classList.add('is-loading');
      return fetchJSON(ROUTES.cartChange, {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: qty })
      }).then(function (cart) {
        var realCount = getRealItemCount(cart);
        updateCartCount(realCount);
        updateSubtotal(cart.total_price);
        updateRewardBar(cart.total_price);
        updateDiscounts(cart);
        updateItems(cart);
        updateRedoSlot(cart);
        updateEmptyState(realCount);
        updateHeaderBubble(realCount);
        drawer.classList.remove('is-loading');
      }).catch(function (err) {
        console.error('Update quantity error:', err);
        showCartError(err);
        drawer.classList.remove('is-loading');
      });
    });
  }

  function removeItem(key, el) {
    // Animate the item out first
    if (el) {
      var item = el.closest('.native-cart-item');
      if (item) {
        item.classList.add('is-removing');
        // Wait for animation then update
        setTimeout(function () {
          updateQuantity(key, 0);
        }, 300);
        return;
      }
    }
    updateQuantity(key, 0);
  }

  function showCartError(err) {
    var msg = (err && err.description) || (err && err.message) || 'Something went wrong. Please try again.';
    var body = qs('.native-cart-body', drawer);
    if (!body) return;
    var existing = qs('.native-cart-error', drawer);
    if (existing) existing.remove();
    var el = document.createElement('div');
    el.className = 'native-cart-error';
    el.textContent = msg;
    body.insertBefore(el, body.firstChild);
    setTimeout(function () { if (el.parentNode) el.remove(); }, 4000);
  }

  function saveNote(text) {
    fetchJSON(ROUTES.cartUpdate, {
      method: 'POST',
      body: JSON.stringify({ note: text })
    });
  }

  // ─── Redo (returns & exchanges) ────────────────────

  function isUSCustomer() {
    return drawer && drawer.getAttribute('data-country') === 'US';
  }

  function getRedoItem(cart) {
    if (!REDO_PRODUCT_ID) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].product_id === REDO_PRODUCT_ID) return cart.items[i];
    }
    return null;
  }

  function getRealItemCount(cart) {
    var count = cart.item_count;
    var redo = getRedoItem(cart);
    if (redo) count -= redo.quantity;
    return count;
  }

  // ensureRedoInCart — commented out, Redo app handles its own injection
  // function ensureRedoInCart(cart) {
  //   if (!isUSCustomer() || redoAddInProgress) return;
  //   var realCount = getRealItemCount(cart);
  //   var redo = getRedoItem(cart);
  //
  //   if (realCount > 0 && !redo) {
  //     redoAddInProgress = true;
  //     fetchJSON(ROUTES.cartAdd, {
  //       method: 'POST',
  //       body: JSON.stringify({ items: [{ id: REDO_VARIANT_ID, quantity: 1 }] })
  //     }).then(function () {
  //       redoAddInProgress = false;
  //       return refreshDrawer();
  //     }).catch(function () {
  //       redoAddInProgress = false;
  //     });
  //   } else if (realCount === 0 && redo) {
  //     fetchJSON(ROUTES.cartChange, {
  //       method: 'POST',
  //       body: JSON.stringify({ id: redo.key, quantity: 0 })
  //     }).then(function () {
  //       return refreshDrawer();
  //     });
  //   }
  // }

  function updateRedoSlot(cart) {
    if (!REDO_ENABLED) return;
    var slot = qs('#native-cart-redo-slot', drawer);
    var skipLink = qs('.native-cart-redo-skip', drawer);
    var redo = getRedoItem(cart);

    if (!redo) {
      if (slot) slot.innerHTML = '';
      if (skipLink) skipLink.style.display = 'none';
      return;
    }

    if (slot) {
      slot.innerHTML =
        '<div class="native-cart-redo-row">' +
          '<div class="native-cart-redo-row__info">' +
            '<span class="native-cart-redo-row__logo">p</span>' +
            '<div>' +
              '<span class="native-cart-redo-row__label">unlimited returns and exchanges</span>' +
              '<button type="button" class="native-cart-redo-row__tooltip" data-action="open-redo-modal" aria-label="Learn more about returns protection">&#9432;</button>' +
              '<div class="native-cart-redo-row__sub">45 day satisfaction guarantee</div>' +
            '</div>' +
          '</div>' +
          '<span class="native-cart-redo-row__price">' + formatMoney(redo.final_price) + '</span>' +
        '</div>';
    }

    if (skipLink) skipLink.style.display = '';
  }

  function checkoutWithoutRedo(cart) {
    var redo = getRedoItem(cart);
    if (!redo) {
      window.location.href = '/checkout';
      return;
    }
    fetchJSON(ROUTES.cartChange, {
      method: 'POST',
      body: JSON.stringify({ id: redo.key, quantity: 0 })
    }).then(function () {
      window.location.href = '/checkout';
    }).catch(function () {
      window.location.href = '/checkout';
    });
  }

  // ─── Upsell (Recommendations API) ─────────────────

  function updateUpsell(cart) {
    if (!UPSELL_ENABLED) return;
    var upsellEl = qs('[data-upsell]', drawer);
    var grid = qs('[data-upsell-grid]', drawer);
    var loader = qs('[data-upsell-loader]', drawer);
    if (!upsellEl || !grid) return;

    // Find first non-Redo product in cart as the seed
    var seedId = null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].product_id !== REDO_PRODUCT_ID) {
        seedId = cart.items[i].product_id;
        break;
      }
    }

    if (!seedId) {
      upsellEl.style.display = 'none';
      grid.innerHTML = '';
      grid.style.display = 'none';
      return;
    }

    // Show upsell with loader while fetching
    upsellEl.style.display = '';
    grid.style.display = 'none';
    grid.innerHTML = '';
    if (loader) loader.style.display = '';

    // Build set of product IDs already in cart for filtering
    var cartIds = {};
    cart.items.forEach(function (item) { cartIds[item.product_id] = true; });

    fetch('/recommendations/products.json?product_id=' + seedId + '&limit=' + Math.min(UPSELL_COUNT + 4, 16) + '&intent=related')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data.products || []).filter(function (p) {
          if (cartIds[p.id]) return false;
          if (p.id === REDO_PRODUCT_ID) return false;
          if (EXCLUDED_PRODUCT_IDS.indexOf(p.id) > -1) return false;
          if (p.handle && p.handle.toLowerCase().indexOf('redo') > -1) return false;
          if (p.vendor && p.vendor.toLowerCase().replace(/[^a-z]/g, '').indexOf('redo') > -1) return false;
          if (!p.available) return false;
          return true;
        }).slice(0, UPSELL_COUNT);

        if (!products.length) {
          upsellEl.style.display = 'none';
          grid.innerHTML = '';
          grid.style.display = 'none';
          if (loader) loader.style.display = 'none';
          return;
        }

        var html = '';
        products.forEach(function (p) {
          var rawImg = (p.images && p.images.length > 1) ? p.images[1] : p.featured_image;
          var imgSrc = rawImg ? rawImg.replace(/(\.(jpg|jpeg|png|gif|webp))/, '_150x$1') : '';
          html += '<a href="' + p.url + '" class="native-cart-upsell__card">' +
            '<div class="native-cart-upsell__card-image">' +
              (imgSrc ? '<img src="' + imgSrc + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="75">' : '') +
            '</div>' +
            '<div class="native-cart-upsell__card-name">' + escapeHtml(p.title) + '</div>' +
            '<div class="native-cart-upsell__card-price">' + formatMoney(p.price) + '</div>' +
          '</a>';
        });

        grid.innerHTML = html;
        if (loader) loader.style.display = 'none';
        grid.style.display = '';
      })
      .catch(function () {
        upsellEl.style.display = 'none';
        grid.innerHTML = '';
        grid.style.display = 'none';
        if (loader) loader.style.display = 'none';
      });
  }

  // ─── Refresh drawer ────────────────────────────────

  var _refreshPending = null;

  function refreshDrawer() {
    if (_refreshPending) return _refreshPending;
    _refreshPending = fetchCart().then(function (cart) {
      var realCount = getRealItemCount(cart);
      updateCartCount(realCount);
      updateSubtotal(cart.total_price);
      updateRewardBar(cart.total_price);
      updateDiscounts(cart);
      updateItems(cart);
      updateRedoSlot(cart);
      updateUpsell(cart);
      updateEmptyState(realCount);
      updateHeaderBubble(realCount);
    }).finally(function () {
      _refreshPending = null;
    });
    return _refreshPending;
  }

  function updateCartCount(count) {
    var els = qsa(SELECTORS.cartCount, drawer);
    els.forEach(function (el) { el.textContent = '(' + count + ')'; });
  }

  function updateSubtotal(totalPrice) {
    var el = qs(SELECTORS.cartSubtotal, drawer);
    if (el) el.textContent = formatMoney(totalPrice);
  }

  function updateRewardBar(totalPrice) {
    if (!TIERS.length) return;
    var bar = qs(SELECTORS.rewardBar, drawer);
    if (!bar) return;

    var fill = qs(SELECTORS.rewardFill, bar);
    var messageEl = qs(SELECTORS.rewardMessage, bar);
    var ticks = qsa(SELECTORS.rewardTicks, bar);
    var labels = qsa(SELECTORS.rewardLabels, bar);
    var tierCount = TIERS.length;
    var segmentWidth = 100 / tierCount;

    // Determine reached tiers and fill width
    var highestReached = -1;
    var fillPct = 0;

    for (var i = 0; i < tierCount; i++) {
      if (totalPrice >= TIERS[i].threshold) {
        highestReached = i;
        fillPct = (i + 1) * segmentWidth;
      } else {
        // Partial progress within this segment
        var segStart = i === 0 ? 0 : TIERS[i - 1].threshold;
        var segEnd = TIERS[i].threshold;
        var segProgress = (totalPrice - segStart) / (segEnd - segStart);
        segProgress = Math.max(0, Math.min(1, segProgress));
        fillPct = (i * segmentWidth) + (segProgress * segmentWidth);
        break;
      }
    }

    // Update fill
    if (fill) fill.style.width = fillPct + '%';

    // Update qualified state
    var allReached = highestReached === tierCount - 1;
    if (allReached) {
      bar.classList.add('is-qualified');
    } else {
      bar.classList.remove('is-qualified');
    }

    // Update ticks and labels
    ticks.forEach(function (tick, idx) {
      if (idx <= highestReached) {
        tick.classList.add('is-reached');
      } else {
        tick.classList.remove('is-reached');
      }
    });

    labels.forEach(function (label, idx) {
      if (idx <= highestReached) {
        label.classList.add('is-reached');
      } else {
        label.classList.remove('is-reached');
      }
    });

    // Update message
    if (messageEl) {
      if (highestReached >= 0) {
        // Threshold met — show "you have X% off, use code:" + copy box
        var reachedTier = TIERS[highestReached];
        messageEl.innerHTML = 'you have <b>' + reachedTier.label + '</b> — use code:';
        if (reachedTier.code) {
          messageEl.innerHTML += buildCodeBox(reachedTier.code);
        }
        // Show progress toward next tier if one exists
        var nextTier = highestReached + 1;
        if (nextTier < tierCount) {
          var nextRemaining = TIERS[nextTier].threshold - totalPrice;
          var nextRemainDollars = Math.ceil(nextRemaining / 100);
          messageEl.innerHTML += '<br><span class="reward-bar__next-hint">$' + nextRemainDollars + ' more for <b>' + TIERS[nextTier].label + '</b></span>';
        }
      } else {
        // Below first tier — show "X away from Y% off" (no code box yet)
        var remaining = TIERS[0].threshold - totalPrice;
        var remainDollars = Math.ceil(remaining / 100);
        messageEl.innerHTML = '$' + remainDollars + ' away from <b>' + TIERS[0].label + '</b>';
      }
    }
  }

  function updateDiscounts(cart) {
    var container = qs(SELECTORS.cartDiscounts, drawer);
    if (!container) {
      // Check if we need to create one
      if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
        var footer = qs('.native-cart-footer', drawer);
        if (footer) {
          var div = document.createElement('div');
          div.className = 'native-cart-footer__discounts';
          div.setAttribute('data-cart-discounts', '');
          footer.insertBefore(div, footer.firstChild);
          container = div;
        }
      }
      if (!container) return;
    }

    if (!cart.cart_level_discount_applications || cart.cart_level_discount_applications.length === 0) {
      container.innerHTML = '';
      container.style.display = 'none';
      return;
    }

    container.style.display = '';
    var html = '';
    cart.cart_level_discount_applications.forEach(function (d) {
      html += '<div class="native-cart-footer__discount-row">' +
        '<span>' + d.title + '</span>' +
        '<span>-' + formatMoney(d.total_allocated_amount) + '</span>' +
        '</div>';
    });
    container.innerHTML = html;
  }

  function updateItems(cart) {
    var container = qs(SELECTORS.cartItems, drawer);
    if (!container) return;

    var html = '';
    cart.items.forEach(function (item) {
      if (item.product_id === REDO_PRODUCT_ID) return; // Redo rendered in footer slot
      html += buildItemHTML(item);
    });
    container.innerHTML = html;
  }

  function buildItemHTML(item) {
    var imgHtml = '';
    if (item.image) {
      var imgSrc = getSizedImageUrl(item.image, '180x');
      imgHtml = '<img src="' + imgSrc + '" alt="' + escapeHtml(item.product_title) + '" width="80" loading="lazy">';
    }

    var variantHtml = '';
    if (item.options_with_values && item.options_with_values.length) {
      item.options_with_values.forEach(function (opt) {
        if (opt.value && opt.value !== 'Default Title') {
          variantHtml += '<div class="native-cart-item__variant">' + escapeHtml(opt.name.toLowerCase()) + ': ' + escapeHtml(opt.value.toLowerCase()) + '</div>';
        }
      });
    } else if (item.variant_title && item.variant_title !== 'Default Title') {
      variantHtml = '<div class="native-cart-item__variant">' + escapeHtml(item.variant_title) + '</div>';
    }

    var sellingPlanHtml = '';
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      sellingPlanHtml = '<div class="native-cart-item__selling-plan">' + escapeHtml(item.selling_plan_allocation.selling_plan.name) + '</div>';
    }

    var propsHtml = '';
    if (item.properties) {
      var propEntries = [];
      for (var key in item.properties) {
        if (item.properties.hasOwnProperty(key) && item.properties[key] && key.charAt(0) !== '_') {
          if (key === 'preorder') {
            propEntries.push('<span class="cart-preorder-tag">preorder · ' + escapeHtml(item.properties[key]) + '</span><br>');
          } else {
            propEntries.push('<span>' + escapeHtml(key) + ': ' + escapeHtml(item.properties[key]) + '</span><br>');
          }
        }
      }
      if (propEntries.length) {
        propsHtml = '<div class="native-cart-item__properties">' + propEntries.join('') + '</div>';
      }
    }

    var priceHtml = '';
    if (item.original_price !== item.final_price) {
      priceHtml = '<span class="native-cart-item__price--original">' + formatMoney(item.original_price) + '</span>' +
        '<span class="native-cart-item__price--sale">' + formatMoney(item.final_price) + '</span>';
    } else {
      priceHtml = '<span>' + formatMoney(item.original_price) + '</span>';
    }

    if (item.line_level_discount_allocations && item.line_level_discount_allocations.length) {
      item.line_level_discount_allocations.forEach(function (d) {
        priceHtml += '<div class="native-cart-item__line-discount">' +
          escapeHtml(d.discount_application.title) + ' (-' + formatMoney(d.amount) + ')' +
          '</div>';
      });
    }

    return '<div class="native-cart-item" data-key="' + item.key + '" data-item-id="' + item.id + '">' +
      '<div class="native-cart-item__image"><a href="' + item.url + '">' + imgHtml + '</a></div>' +
      '<div class="native-cart-item__details">' +
        '<div class="native-cart-item__name"><a href="' + item.url + '">' + escapeHtml(item.product_title) + '</a></div>' +
        variantHtml + sellingPlanHtml + propsHtml +
        '<div class="native-cart-item__bottom">' +
          '<div class="native-cart-qty">' +
            '<button type="button" data-action="decrease" data-key="' + item.key + '" aria-label="Decrease quantity">' +
              '<svg viewBox="0 0 20 20" fill="#444"><path d="M17.543 11.029H2.1A1.032 1.032 0 0 1 1.071 10c0-.566.463-1.029 1.029-1.029h15.443c.566 0 1.029.463 1.029 1.029 0 .566-.463 1.029-1.029 1.029z"/></svg>' +
            '</button>' +
            '<input type="number" value="' + item.quantity + '" min="1" data-key="' + item.key + '" data-action="quantity-input" aria-label="Quantity">' +
            '<button type="button" data-action="increase" data-key="' + item.key + '" aria-label="Increase quantity">' +
              '<svg viewBox="0 0 20 20" fill="#444"><path d="M17.409 8.929h-6.695V2.258c0-.566-.506-1.029-1.071-1.029s-1.071.463-1.071 1.029v6.671H1.967C1.401 8.929.938 9.435.938 10s.463 1.071 1.029 1.071h6.605V17.7c0 .566.506 1.029 1.071 1.029s1.071-.463 1.071-1.029v-6.629h6.695c.566 0 1.029-.506 1.029-1.071s-.463-1.071-1.029-1.071z"/></svg>' +
            '</button>' +
          '</div>' +
          '<div class="native-cart-item__price">' + priceHtml + '</div>' +
        '</div>' +
      '</div>' +
      '<button type="button" class="native-cart-item__remove" data-action="remove" data-key="' + item.key + '" aria-label="Remove item">' +
        '<svg viewBox="0 0 64 64"><path d="M19 17.61l27.12 27.13m0-27.12L19 44.74" stroke="currentColor" fill="none"/></svg>' +
      '</button>' +
    '</div>';
  }

  function updateEmptyState(count) {
    if (count === 0) {
      drawer.classList.add('is-empty');
      updateEmptyRecs();
    } else {
      drawer.classList.remove('is-empty');
    }
  }

  function updateEmptyRecs() {
    var recsEl = qs('[data-empty-recs]', drawer);
    var grid = qs('[data-empty-recs-grid]', drawer);
    var loader = qs('[data-empty-recs-loader]', drawer);
    if (!recsEl || !grid) return;

    var seedId = null;
    try { seedId = localStorage.getItem('pwt_last_viewed_pid'); } catch (e) {}
    if (!seedId) seedId = recsEl.getAttribute('data-fallback-product-id');
    if (!seedId) {
      recsEl.style.display = 'none';
      return;
    }

    // Show skeleton loader while fetching
    recsEl.style.display = '';
    grid.style.display = 'none';
    grid.innerHTML = '';
    if (loader) loader.style.display = '';

    fetch('/recommendations/products.json?product_id=' + seedId + '&limit=10&intent=related')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data.products || []).filter(function (p) {
          if (p.id === REDO_PRODUCT_ID) return false;
          if (EXCLUDED_PRODUCT_IDS.indexOf(p.id) > -1) return false;
          if (p.handle && p.handle.toLowerCase().indexOf('redo') > -1) return false;
          if (p.vendor && p.vendor.toLowerCase().replace(/[^a-z]/g, '').indexOf('redo') > -1) return false;
          if (!p.available) return false;
          return true;
        }).slice(0, 4);

        if (!products.length) {
          recsEl.style.display = 'none';
          grid.innerHTML = '';
          grid.style.display = 'none';
          if (loader) loader.style.display = 'none';
          return;
        }

        var html = '';
        products.forEach(function (p) {
          var imgSrc = p.featured_image ? getSizedImageUrl(p.featured_image, '150x') : '';
          html += '<a href="' + p.url + '" class="native-cart-empty__rec-card">' +
            '<div class="native-cart-empty__rec-card-image">' +
              (imgSrc ? '<img src="' + imgSrc + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="105">' : '') +
            '</div>' +
            '<div class="native-cart-empty__rec-card-name">' + escapeHtml(p.title) + '</div>' +
            '<div class="native-cart-empty__rec-card-price">' + formatMoney(p.price) + '</div>' +
          '</a>';
        });

        grid.innerHTML = html;
        if (loader) loader.style.display = 'none';
        grid.style.display = '';
      })
      .catch(function () {
        recsEl.style.display = 'none';
        grid.innerHTML = '';
        grid.style.display = 'none';
        if (loader) loader.style.display = 'none';
      });
  }

  function updateHeaderBubble(count) {
    var bubbles = qsa(SELECTORS.headerBubble);
    bubbles.forEach(function (el) {
      if (count > 0) {
        el.classList.add('cart-link__bubble--visible');
        el.textContent = count;
      } else {
        el.classList.remove('cart-link__bubble--visible');
        el.textContent = '';
      }
    });
  }

  // ─── Image URL helper ──────────────────────────────

  function getSizedImageUrl(src, size) {
    if (!src) return '';
    // Shopify image URL pattern: replace _WIDTHx or add before extension
    var match = src.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i);
    if (match) {
      var ext = match[0];
      return src.replace(ext, '_' + size + ext);
    }
    return src;
  }

  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // ─── Note toggle ───────────────────────────────────

  function toggleNote() {
    var wrapper = qs(SELECTORS.noteWrapper, drawer);
    if (wrapper) wrapper.classList.toggle('is-open');
  }

  // ─── Event delegation ──────────────────────────────

  function handleDrawerClick(e) {
    var target = e.target;
    var actionEl = target.closest('[data-action]');
    if (!actionEl) return;

    var action = actionEl.getAttribute('data-action');
    var key = actionEl.getAttribute('data-key');

    switch (action) {
      case 'close-drawer':
        closeDrawer();
        break;

      case 'remove':
        e.preventDefault();
        removeItem(key, actionEl);
        break;

      case 'decrease':
        e.preventDefault();
        var input = actionEl.closest('.native-cart-qty').querySelector('input');
        var currentQty = parseInt(input.value, 10);
        if (currentQty <= 1) {
          removeItem(key, actionEl);
        } else {
          updateQuantity(key, currentQty - 1);
        }
        break;

      case 'increase':
        e.preventDefault();
        var inputPlus = actionEl.closest('.native-cart-qty').querySelector('input');
        var curQty = parseInt(inputPlus.value, 10);
        updateQuantity(key, curQty + 1);
        break;

      case 'toggle-note':
        e.preventDefault();
        toggleNote();
        break;

      case 'open-redo-modal':
        e.preventDefault();
        var modal = qs('[data-redo-modal]', drawer);
        if (modal) modal.style.display = '';
        break;

      case 'close-redo-modal':
        e.preventDefault();
        var modalClose = qs('[data-redo-modal]', drawer);
        if (modalClose) modalClose.style.display = 'none';
        break;

      case 'checkout-without-redo':
        e.preventDefault();
        fetchCart().then(function (cart) {
          checkoutWithoutRedo(cart);
        });
        break;

    }
  }

  function handleQuantityChange(e) {
    if (e.target.getAttribute('data-action') !== 'quantity-input') return;
    var key = e.target.getAttribute('data-key');
    var val = parseInt(e.target.value, 10);
    if (isNaN(val) || val < 0) return;
    if (val === 0) {
      removeItem(key, e.target);
    } else {
      updateQuantity(key, val);
    }
  }

  function handleNoteChange(e) {
    if (noteTimer) clearTimeout(noteTimer);
    noteTimer = setTimeout(function () {
      saveNote(e.target.value);
    }, 600);
  }

  // ─── Intercept add-to-cart forms ───────────────────

  function handleAddToCartForm(form) {
      var formData = new FormData(form);
      var item = {};

      // Extract variant ID
      var idInput = form.querySelector('[name="id"]');
      if (idInput) item.id = parseInt(idInput.value, 10);

      // Extract quantity
      var qtyInput = form.querySelector('[name="quantity"]');
      item.quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;

      // Extract properties
      var props = {};
      formData.forEach(function (val, key) {
        if (key.startsWith('properties[')) {
          var propKey = key.replace('properties[', '').replace(']', '');
          props[propKey] = val;
        }
      });
      if (Object.keys(props).length) item.properties = props;

      // Extract selling plan
      var sellingPlan = formData.get('selling_plan');
      if (sellingPlan) item.selling_plan = sellingPlan;

      if (!item.id) return;

      // Prevent double-submit
      if (form.getAttribute('data-ncd-adding')) return;
      form.setAttribute('data-ncd-adding', 'true');

      // Show loading state on button — target inner text span to preserve DOM structure
      var submitBtn = form.querySelector('[type="submit"]');
      var textSpan = submitBtn ? submitBtn.querySelector('[data-add-to-cart-text]') : null;
      var textEl = textSpan || submitBtn;
      var originalDefaultText = textSpan ? textSpan.getAttribute('data-default-text') : null;

      // IMPORTANT: Do NOT set submitBtn.disabled = true — that triggers the
      // theme CSS rule `.add-to-cart[disabled] ~ .notify-wrapper` which shows
      // the "notify me" button. Instead use is-loading class + form flag.
      if (submitBtn) submitBtn.classList.add('is-loading');

      // Swap data-default-text so theme.js updateCartButton (which reads
      // dataset.defaultText and writes it to textContent) reinforces our state
      if (textSpan) textSpan.setAttribute('data-default-text', 'adding...');
      if (textEl) textEl.textContent = 'adding...';

      // Hide the notify wrapper for the duration of our flow
      var notifyWrapper = form.parentElement ? form.parentElement.querySelector('.notify-wrapper') : null;
      if (!notifyWrapper) notifyWrapper = form.closest('.product-block') ? form.closest('.product-block').querySelector('.notify-wrapper') : null;
      if (notifyWrapper) notifyWrapper.style.display = 'none';

      suppressThirdPartyDrawers();

      function restoreButton(showAdded) {
        if (submitBtn) submitBtn.classList.remove('is-loading');
        if (textSpan && originalDefaultText) textSpan.setAttribute('data-default-text', originalDefaultText);
        if (showAdded && textEl) {
          textEl.textContent = 'added!';
          if (textSpan) textSpan.setAttribute('data-default-text', 'added!');
          setTimeout(function () {
            if (textEl) textEl.textContent = originalDefaultText || 'add to bag';
            if (textSpan && originalDefaultText) textSpan.setAttribute('data-default-text', originalDefaultText);
            if (notifyWrapper) notifyWrapper.style.display = '';
            form.removeAttribute('data-ncd-adding');
          }, 1500);
        } else {
          if (textEl && originalDefaultText) textEl.textContent = originalDefaultText;
          if (notifyWrapper) notifyWrapper.style.display = '';
          form.removeAttribute('data-ncd-adding');
        }
      }

      enqueueCartOp(function () {
        return fetchJSON(ROUTES.cartAdd, {
          method: 'POST',
          body: JSON.stringify({ items: [item] })
        }).then(function () {
          return refreshDrawer();
        }).then(function () {
          openDrawer();
          restoreButton(true);
        }).catch(function (err) {
          console.error('Add to cart error:', err);
          restoreButton(false);
          return refreshDrawer().then(function () {
            openDrawer();
            showCartError(err);
          });
        });
      });
  }

  function interceptAddToCart() {
    // Capture-phase click handler — fires before any other click handler
    // Needed because something (app/script) calls preventDefault on button clicks,
    // preventing the native form submit from ever firing
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('form[action*="/cart/add"] [type="submit"]');
      if (!btn) return;
      var form = btn.closest('form[action*="/cart/add"]');
      if (!form) return;
      if (e.ctrlKey || e.metaKey) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      handleAddToCartForm(form);
    }, true);

    // Also keep submit listener as a fallback (e.g. programmatic submits)
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;

      if (e.ctrlKey || e.metaKey) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      handleAddToCartForm(form);
    }, true);
  }

  // ─── Suppress third-party cart drawers ───────────────

  function nukeSlideCart() {
    var killed = false;
    qsa('#slidecarthq, .slidecarthq, .slidecarthq-overlay, [id*="slidecart"], [class*="slidecart"]').forEach(function (el) {
      el.remove();
      killed = true;
    });
    return killed;
  }

  function suppressThirdPartyDrawers() {
    nukeSlideCart();

    // Hide Archetype built-in drawer if present
    var archetypeDrawer = qs('#CartDrawer');
    if (archetypeDrawer) {
      archetypeDrawer.style.cssText = 'display:none!important;visibility:hidden!important;';
    }
  }

  // Persistent MutationObserver — removes SlideCart the instant it appears
  function watchForSlideCart() {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          // typeof guard: a form containing <input name="id"> shadows node.id with the element itself
          if (typeof node.id === 'string' && node.id.toLowerCase().indexOf('slidecart') > -1 ||
              node.className && typeof node.className === 'string' && node.className.toLowerCase().indexOf('slidecart') > -1) {
            node.remove();
            continue;
          }
          // Check children in case SlideCart is nested inside a wrapper
          var nested = node.querySelectorAll ? node.querySelectorAll('[id*="slidecart"], [class*="slidecart"]') : [];
          for (var k = 0; k < nested.length; k++) { nested[k].remove(); }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ─── Cart icon click ───────────────────────────────

  function bindCartIconLinks() {
    // Delegate on document for both original and sticky header clones
    document.addEventListener('click', function (e) {
      var cartLink = e.target.closest('.js-native-cart-open');
      if (!cartLink) return;

      e.preventDefault();
      suppressThirdPartyDrawers();

      if (isOpen) {
        closeDrawer();
      } else {
        refreshDrawer().then(openDrawer);
      }
    });
  }

  // ─── Keyboard ──────────────────────────────────────

  function handleKeydown(e) {
    if (e.key === 'Escape' && isOpen) {
      closeDrawer();
    }
  }

  // ─── Init ──────────────────────────────────────────

  function init() {
    drawer = qs(SELECTORS.drawer);
    if (!drawer) return;

    // Read configuration from data attributes (set by section settings)
    try { TIERS = JSON.parse(drawer.getAttribute('data-tiers') || '[]'); } catch (e) { TIERS = []; }
    REDO_ENABLED = drawer.getAttribute('data-redo-enable') === 'true';
    REDO_PRODUCT_ID = parseInt(drawer.getAttribute('data-redo-product-id'), 10) || 9923270607158;
    REDO_VARIANT_ID = parseInt(drawer.getAttribute('data-redo-variant-id'), 10) || 0;
    UPSELL_ENABLED = drawer.getAttribute('data-upsell-enable') === 'true';
    UPSELL_COUNT = parseInt(drawer.getAttribute('data-upsell-count'), 10) || 4;
    NOTE_ENABLED = drawer.getAttribute('data-note-enable') === 'true';

    // Event delegation on drawer
    drawer.addEventListener('click', handleDrawerClick);
    drawer.addEventListener('change', handleQuantityChange);

    // Note debounced save
    var noteInput = qs(SELECTORS.noteInput, drawer);
    if (noteInput) noteInput.addEventListener('input', handleNoteChange);

    // Nuke SlideCart and watch for it permanently
    suppressThirdPartyDrawers();
    watchForSlideCart();

    // Cart icon links
    bindCartIconLinks();

    // Intercept add-to-cart forms
    interceptAddToCart();

    // Escape key
    document.addEventListener('keydown', handleKeydown);

    // Track last viewed product for empty cart recs
    if (window.meta && window.meta.product && window.meta.product.id) {
      try { localStorage.setItem('pwt_last_viewed_pid', window.meta.product.id); } catch (e) {}
    }

    // Initialize reward bar from current cart
    fetchCart().then(function (cart) {
      var realCount = getRealItemCount(cart);
      updateRewardBar(cart.total_price);
      updateHeaderBubble(realCount);
      updateCartCount(realCount);
    });
  }

  // ─── Public API ────────────────────────────────────

  window.NativeCart = {
    open: function () {
      suppressThirdPartyDrawers();
      if (isOpen) { closeDrawer(); return; }
      refreshDrawer().then(openDrawer);
    },
    close: function () { closeDrawer(); },
    refresh: function () { return refreshDrawer(); },
    add: function (variantId, qty, props, sellingPlan) { return addToCart(variantId, qty, props, sellingPlan); },
    // Re-read reward tiers from data-tiers and re-render the reward bar.
    // Used by the shipping-threshold A/B snippet after it overrides data-tiers.
    applyTiers: function () {
      if (!drawer) return;
      try { TIERS = JSON.parse(drawer.getAttribute('data-tiers') || '[]'); } catch (e) { TIERS = []; }
      fetchCart().then(function (cart) { updateRewardBar(cart.total_price); });
    }
  };

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
